import { formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { useDispatchWeekQuery } from '../../dispatch/useDispatchWeekQuery.js';

import { HOME_CARD_LINK, HOME_CARD_ROW, HomeCard, homeCardStatus } from './HomeCard.js';

/** Eight rows is about a day's crew before the card needs its own scrollbar. */
const MAX_ROWS = 8;

/**
 * "Today" — who is assigned to what, on the day the SERVER says today is.
 *
 * ⚠ THE DAY COMES FROM THE RESPONSE, not from the browser. `GET /api/v1/dispatch` resolves the
 * Monday-start week and returns its own `today` (`DispatchWeekResponse.today`). A dispatcher whose
 * laptop clock is wrong, or who is in a different zone from the tenant, would otherwise read an
 * empty board and believe nobody is working. Filtering on the server's own string cannot disagree
 * with the week the server just built.
 *
 * The same query the board screen uses, so opening Dispatch after this costs no second request and
 * the two screens can never show different assignments.
 *
 * The rows sit in TWO columns once the card is wide. As one full-width list, a name sat on the left
 * rim and its time on the right with most of a screen of nothing between them, which is a long way
 * for an eye to travel to pair two facts that belong together.
 */
export function DispatcherToday() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const query = useDispatchWeekQuery({});

  const today = query.data?.today ?? null;
  const all = (query.data?.assignments ?? []).filter(
    (assignment) => today !== null && assignment.scheduledDate === today,
  );
  const shown = all.slice(0, MAX_ROWS);

  /** A start time is optional on an assignment, so the row says "all day" rather than nothing. */
  const timeLabel = (start: string | null, end: string | null): string => {
    if (!start) return t('home:dispatcher.today.allDay');
    const at = (value: string) =>
      formatUiDate(`1970-01-01T${value}`, locale, {
        hour: 'numeric',
        minute: '2-digit',
      });
    return end ? t('home:dispatcher.today.span', { start: at(start), end: at(end) }) : at(start);
  };

  return (
    <HomeCard
      headingId="dispatcher-today-heading"
      title={t('home:dispatcher.today.heading')}
      action={
        <Link to="/dispatch" search={{ week: undefined }} className={HOME_CARD_LINK}>
          {t('home:dispatcher.today.openBoard')}
        </Link>
      }
      status={homeCardStatus(query, shown.length)}
      errorText={t('home:dispatcher.today.unavailable')}
      emptyText={t('home:dispatcher.today.empty')}
      onRetry={() => void query.refetch()}
    >
      <ul className="mt-3 grid gap-2 lg:grid-cols-2">
        {shown.map((assignment) => (
          <li key={assignment.id} className={HOME_CARD_ROW}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="font-medium text-foreground text-sm">{assignment.employeeName}</span>
              <span className="font-mono text-muted-foreground text-xs tabular-nums">
                {timeLabel(assignment.startTime, assignment.endTime)}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">
              {assignment.jobName ?? t('home:dispatcher.today.noJob')}
              {assignment.clientName ? ` · ${assignment.clientName}` : ''}
            </span>
          </li>
        ))}
        {all.length > shown.length ? (
          <li className="text-muted-foreground text-xs lg:col-span-2">
            {t('home:dispatcher.today.more', { count: all.length - shown.length })}
          </li>
        ) : null}
      </ul>
    </HomeCard>
  );
}
